import React from "react";
import { Button } from "../components/ui/button";

export const SummaryPanel = ({
  t,
  liveSummary,
  nGuests,
  formatWithSpaces,
  loading,
  error,
  code,
  loadCode,
  setLoadCode,
  onReset,
  onSaveConfig,
  onLoadConfig,
}) => (
  <aside className="mt-6 md:mt-0 md:block">
    <div className="md:sticky md:top-6">
      <div className="rounded-2xl border border-slate-900/40 bg-slate-950 text-slate-100 shadow-2xl px-5 py-5">
        <div className="mb-3 space-y-1">
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
            {t("summary.title")}
          </div>
          <div className="text-3xl font-semibold">
            {formatWithSpaces(liveSummary.total)}
          </div>
          <div className="text-xs text-slate-400">
            ≈ {formatWithSpaces(liveSummary.perGuest)} {t("summary.perGuest")} • {nGuests || 0}{" "}
            {t("summary.guestsSuffix")}
          </div>
        </div>

        {liveSummary.items.length > 0 ? (
          <ul className="mt-2 max-h-64 space-y-1 overflow-y-auto text-xs">
            {liveSummary.items.map((item, index) => (
              <li
                key={index}
                className="flex items-center justify-between border-b border-slate-800/60 pb-1 last:border-none last:pb-0"
              >
                <span className="text-slate-200">{item.label}</span>
                <span className="font-mono text-amber-300">
                  {formatWithSpaces(item.amount)}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-2 text-xs text-slate-500">
            {t("summary.emptyText")}
          </p>
        )}

        {/* Actions */}
        <div className="mt-4 border-t border-slate-800/60 pt-4 space-y-4">
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
            {t("actions.cardTitle")}
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onReset}
              disabled={loading}
            >
              {t("actions.resetAll")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => onSaveConfig({ downloadPdf: false })}
              disabled={loading || liveSummary.items.length === 0}
            >
              {t("actions.generateCode")}
            </Button>
            <Button
              type="button"
              onClick={() => onSaveConfig({ downloadPdf: true })}
              disabled={loading || liveSummary.items.length === 0}
            >
              {t("actions.generatePdf")}
            </Button>
            <button
              type="button"
              disabled
              title="Work in progress"
              className="inline-flex h-9 items-center justify-center whitespace-nowrap rounded-md border border-slate-600 bg-slate-900 px-3 text-sm font-medium text-slate-400 opacity-60 cursor-not-allowed"
            >
              {t("actions.generateExcel")}
            </button>
          </div>

          {error && (
            <div className="rounded-md border border-red-500/40 bg-red-950/40 px-3 py-2 text-xs text-red-200">
              {error}
            </div>
          )}

          {code && (
            <div className="space-y-1">
              <div className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">
                {t("actions.configCodeLabel")}
              </div>
              <div className="flex items-center justify-between gap-2 rounded-md bg-slate-900 px-3 py-2 text-xs text-slate-100">
                <span className="font-mono tracking-[0.18em]">{code}</span>
                <button
                  type="button"
                  onClick={() => {
                    if (navigator.clipboard?.writeText) {
                      navigator.clipboard.writeText(code).catch(() => {});
                    }
                  }}
                  className="rounded-md border border-slate-500/60 bg-slate-800 px-2 py-1 text-[10px] font-medium text-slate-100 hover:bg-slate-700"
                >
                  {t("actions.copyCode")}
                </button>
              </div>
            </div>
          )}

          <div className="mt-2 space-y-2 border-t border-dashed border-slate-800/60 pt-3">
            <div className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">
              {t("actions.loadTitle")}
            </div>
            <div className="flex flex-wrap gap-2">
              <input
                className="h-9 flex-1 rounded-md border border-input bg-slate-900 px-3 py-1 text-sm text-slate-100 outline-none focus-visible:ring-2 focus-visible:ring-rose-400"
                placeholder={t("actions.loadPlaceholder")}
                value={loadCode}
                onChange={(e) => setLoadCode(e.target.value)}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onLoadConfig()}
                disabled={loading}
              >
                {t("actions.loadButton")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </aside>
);
